/**
 * Day-ahead price discovery + parsing — mirrors the TB-EMS dashboard
 * (`ems-dashboard/.../energy-prices.tsx`). The EMS server's
 * `market_provisioner` + `market_prices` analytics provision an asset
 * per bidding zone (profile `electricity_bidding_zone`) and write:
 *
 *   - SERVER_SCOPE attribute `next_day_prices` — JSON envelope with
 *     `{ date, timezone, values: number[24], unit, fetched_at, ... }`.
 *     Hour 0 of the array maps to 00:00 LOCAL of `date` in `timezone`.
 *
 *   - SERVER_SCOPE attribute `recent_days_ring` — JSON `RingDay[]`,
 *     one entry per recent day (typically 14). Each entry is the same
 *     envelope shape (date + timezone + values[24]). Used to backfill
 *     "yesterday" / earlier dates on the price curve.
 *
 * The SPA cares about three windows: yesterday → today → tomorrow.
 * We assemble the curve by union-ing `recent_days_ring` with
 * `next_day_prices`, then converting each (date+timezone+hour) tuple
 * into an absolute epoch-ms ts.
 *
 * Discovery: we currently take the FIRST zone the user can see. A future
 * enhancement could pin the zone via a site attribute (e.g.
 * `bidding_zone_id`) — for now the SPA matches TB-EMS's "first
 * accessible zone" default.
 */
import type { TbHttp } from "./http";
import { getAttributes } from "./telemetry";
import type { EntityRef, PageData, TbEntity } from "./types";
import type { PricePoint } from "../types";

const ZONE_ASSET_TYPE = "electricity_bidding_zone";
const ZONE_ATTR_KEYS = ["next_day_prices", "recent_days_ring"] as const;

interface PriceEnvelope {
  date: string;
  timezone: string;
  currency?: string;
  unit?: string;
  values: Array<number | null>;
  fetched_at?: string;
  source?: string;
}

interface RingDay extends PriceEnvelope { /* same shape */ }

/**
 * Discover the bidding-zone asset(s) accessible to the current user and
 * pull the day-ahead curves. Returns flattened PricePoint[] spanning
 * roughly the last 14 days + tomorrow.
 *
 * Returns `[]` when:
 *   - No zone asset is visible (tenant hasn't provisioned market_prices)
 *   - All envelopes failed to parse (corrupt JSON)
 *
 * The caller (RevenuePage / HistoricalPage) handles the empty case with
 * an EmptyState panel — no crashes.
 */
export async function discoverDayAheadPrices(
  http: TbHttp,
  startTs: number,
  endTs: number,
): Promise<PricePoint[]> {
  const zone = await pickZoneAsset(http);
  if (!zone) return [];
  const attrs = await getAttributes(http, { type: "ASSET", id: zone.id.id }, "SERVER_SCOPE", [...ZONE_ATTR_KEYS]).catch(() => []);
  const envelopes: PriceEnvelope[] = [];
  const ring = readJson<RingDay[]>(attrs, "recent_days_ring");
  if (ring) envelopes.push(...ring);
  const next = readJson<PriceEnvelope>(attrs, "next_day_prices");
  if (next) envelopes.push(next);
  const points: PricePoint[] = [];
  for (const e of envelopes) {
    points.push(...envelopeToPoints(e));
  }
  // Dedupe + filter to requested window. Multiple envelopes can cover
  // the same date (today appears in BOTH ring and next_day — last write
  // wins so the most recent fetched_at takes precedence).
  const byTs = new Map<number, number>();
  for (const p of points) byTs.set(p.ts, p.price);
  return [...byTs.entries()]
    .filter(([ts]) => ts >= startTs - 24 * 3_600_000 && ts <= endTs + 24 * 3_600_000)
    .sort((a, b) => a[0] - b[0])
    .map(([ts, price]) => ({ ts, price }));
}

/**
 * Find a bidding-zone asset. Pattern matches TB-EMS: server-side filter
 * by profile, take the first accessible match. Customer users see only
 * their own zones; tenant admins see all.
 */
async function pickZoneAsset(http: TbHttp): Promise<TbEntity | null> {
  try {
    const r = await http.get<PageData<TbEntity>>(
      `/api/tenant/assets?pageSize=20&type=${encodeURIComponent(ZONE_ASSET_TYPE)}`,
    );
    if (r.data.length) return r.data[0];
  } catch {
    // Tenant endpoint may 403 for customer users — fall through to the
    // customer-scoped endpoint.
  }
  // Customer-scoped lookup (the inverse path TB-EMS uses on customer logins).
  try {
    const me = await http.get<{ customerId?: { id: string } }>(`/api/auth/user`);
    if (!me.customerId?.id) return null;
    const r = await http.get<PageData<TbEntity>>(
      `/api/customer/${me.customerId.id}/assets?pageSize=20&type=${encodeURIComponent(ZONE_ASSET_TYPE)}`,
    );
    return r.data[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * Convert a `{date, timezone, values[24]}` envelope into 24 epoch-ms
 * PricePoints. Hour i corresponds to local time `date T HH:00` in
 * `timezone`. We build a Date by parsing the ISO-like local string in
 * the target timezone using Intl to compute the offset.
 *
 * `values` may contain `null` entries (intraday envelopes have these);
 * we skip them rather than emit NaN.
 */
function envelopeToPoints(e: PriceEnvelope): PricePoint[] {
  if (!e?.date || !Array.isArray(e.values)) return [];
  const out: PricePoint[] = [];
  for (let h = 0; h < e.values.length; h++) {
    const v = e.values[h];
    if (v === null || v === undefined || !Number.isFinite(v)) continue;
    const ts = localDateHourToEpochMs(e.date, h, e.timezone || "UTC");
    if (Number.isFinite(ts)) out.push({ ts, price: v as number });
  }
  return out;
}

/**
 * Compute the absolute epoch-ms for `YYYY-MM-DDTHH:00` in `tz`. Uses
 * Intl.DateTimeFormat to extract the tz offset at that wall-clock —
 * DST-safe.
 */
function localDateHourToEpochMs(date: string, hour: number, tz: string): number {
  // Parse the date components from the YYYY-MM-DD string.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return NaN;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  // First guess: assume UTC, then compute the tz offset at that moment
  // and correct. Doing one round-trip is enough for civil time.
  const guess = Date.UTC(y, mo - 1, d, hour, 0, 0);
  const offsetMin = tzOffsetMinutes(guess, tz);
  return guess - offsetMin * 60_000;
}

/** Offset in minutes that `tz` is AHEAD of UTC at the given instant. */
function tzOffsetMinutes(epochMs: number, tz: string): number {
  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    });
    const parts = dtf.formatToParts(new Date(epochMs));
    const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
    const wall = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
    return (wall - epochMs) / 60_000;
  } catch {
    return 0;
  }
}

function readJson<T>(attrs: Array<{ key: string; value: unknown }>, key: string): T | null {
  const a = attrs.find((x) => x.key === key);
  if (!a) return null;
  if (typeof a.value === "string") {
    try { return JSON.parse(a.value) as T; } catch { return null; }
  }
  if (typeof a.value === "object" && a.value !== null) return a.value as T;
  return null;
}

/** Re-export so the adapter can scope discovery to a specific zone in future. */
export type { PriceEnvelope, RingDay };
export const PRICE_ZONE_ASSET_TYPE = ZONE_ASSET_TYPE;
export function isZoneAsset(asset: { type?: string } | null | undefined): boolean {
  return asset?.type === ZONE_ASSET_TYPE;
}
export type ZoneRef = EntityRef;
